import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  documentTemplate,
  type DocumentEntity,
  type LocationNode,
  type MeasurementSeries,
} from '@oscom/shared';
import { getCustomer } from '../api/customers';
import { listDocuments } from '../api/documents';
import { listLocations } from '../api/locations';
import { listMeasurementSeries } from '../api/measurements';
import { useSession } from '../auth/SessionProvider';
import { CustomerContextNav } from '../components/CustomerContextNav';
import { DocumentCreateDialog } from '../components/DocumentCreateDialog';
import { Layout } from '../components/Layout';
import { locationPath } from '../plans/locationPath';
import { NotFoundPage } from './NotFoundPage';

/** Local date and time of a UTC timestamp, e.g. "31.10.2026, 10:30". */
export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * Documents of one customer. Reading needs `documents:read`, creating
 * `documents:write`. Exists only while the `documents` switch is on.
 */
export function DocumentsPage(): ReactNode {
  const { session } = useSession();
  const navigate = useNavigate();
  const { customerId: customerIdParam } = useParams<{ customerId: string }>();
  const enabled = session?.status === 'authenticated' && session.me.features?.documents === true;
  const measurementsEnabled =
    session?.status === 'authenticated' && session.me.features?.measurements === true;

  const [customerName, setCustomerName] = useState<string | undefined>(undefined);
  const [locations, setLocations] = useState<LocationNode[]>([]);
  const [series, setSeries] = useState<MeasurementSeries[]>([]);
  const [documents, setDocuments] = useState<DocumentEntity[] | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!customerIdParam || !enabled) return;
    let cancelled = false;
    Promise.all([
      getCustomer(customerIdParam),
      listLocations(customerIdParam),
      listDocuments(customerIdParam),
      measurementsEnabled
        ? listMeasurementSeries(customerIdParam)
        : Promise.resolve({ series: [] }),
    ])
      .then(([customer, locationResponse, documentResponse, seriesResponse]) => {
        if (cancelled) return;
        setCustomerName(customer.name);
        setLocations(locationResponse.locations);
        setDocuments(documentResponse.documents);
        setSeries(seriesResponse.series);
        setLoadError(undefined);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setLoadError(
            caught instanceof Error ? caught.message : 'Dokumente konnten nicht geladen werden.',
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [customerIdParam, enabled, measurementsEnabled]);

  if (session?.status !== 'authenticated') return null;
  if (!enabled) return <NotFoundPage />;
  if (!customerIdParam) return null;
  const { me } = session;
  const customerId = customerIdParam;
  const canWrite = me.permissions.includes('documents:write');
  const base = `/kunden/${encodeURIComponent(customerId)}/dokumente`;
  const locationsById = new Map(locations.map((node) => [node.id, node]));

  return (
    <Layout user={{ displayName: me.displayName, role: me.role }} showLogout>
      <section className="stack">
        {customerName ? (
          <CustomerContextNav
            customerId={customerId}
            customerName={customerName}
            features={me.features}
          />
        ) : null}
        <div className="card">
          <h1>Dokumente</h1>
          {canWrite && !creating ? (
            <p>
              <button
                type="button"
                className="button button--primary"
                onClick={() => setCreating(true)}
              >
                Neues Dokument
              </button>
            </p>
          ) : null}
          {loadError ? (
            <p className="notice notice--warning">{loadError}</p>
          ) : documents === undefined ? (
            <p className="muted" role="status" aria-live="polite">
              Dokumente werden geladen ...
            </p>
          ) : documents.length === 0 ? (
            <p className="muted">Noch kein Dokument angelegt.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th scope="col">Titel</th>
                    <th scope="col">Vorlage</th>
                    <th scope="col">Ort</th>
                    <th scope="col">Letzte Änderung</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((entry) => (
                    <tr key={entry.id}>
                      <td>
                        <Link to={`${base}/${encodeURIComponent(entry.id)}`}>{entry.title}</Link>
                      </td>
                      <td>
                        {documentTemplate(entry.documentTypeKey)?.name ?? entry.documentTypeKey}
                      </td>
                      <td>{locationPath(entry.siteLocationId, locationsById)}</td>
                      <td>{formatDateTime(entry.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {canWrite && creating ? (
          <DocumentCreateDialog
            customerId={customerId}
            locations={locations}
            series={series}
            onCancel={() => setCreating(false)}
            onDone={(document) => navigate(`${base}/${encodeURIComponent(document.id)}`)}
          />
        ) : null}
      </section>
    </Layout>
  );
}
